import { useFlag } from '@unleash/proxy-client-react'
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined'
import { IconButton, Tooltip, useTheme } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { useParams } from 'react-router-dom'
import { activeAlbumIdVar, modalVar } from '../../../../../providers/apollo/cache'
import useBucketDownload from '../../../hooks/useBucketDownload'
import useSelectedAlbums from '../../../hooks/useSelectedAlbums'
import useBucket from '../../../hooks/useBucket'

export default function AlbumDownloadIcon() {
  const { bucketId, albumId } = useParams<{ bucketId: string; albumId: string }>()
  const useUpdatedAlbumActions = useFlag('useUpdatedAlbumActions')
  const { isFreemiumBucket, currentUserIsOwner } = useBucket()
  const { selectedAlbums } = useSelectedAlbums()
  const { downloadAlbums, downloadMultipleMediaResults } = useBucketDownload()
  const { loading } = downloadMultipleMediaResults
  const containsRootAlbum = Array.from(selectedAlbums.keys()).includes(bucketId as string)
  const disabled = !bucketId || containsRootAlbum || (selectedAlbums.size < 1 && !albumId)
  const freemiumRestricted = isFreemiumBucket && !disabled
  const theme = useTheme()

  const onClick = () => {
    if (disabled) {
      return
    }

    if (isFreemiumBucket) {
      modalVar(currentUserIsOwner ? 'upsellModal' : 'premiumFeature')
      return
    }

    const selectedAlbumIds = Array.from(selectedAlbums.keys())

    if (albumId) {
      activeAlbumIdVar(albumId)
      downloadAlbums(bucketId, [albumId])
    } else if (selectedAlbumIds.length > 0) {
      downloadAlbums(bucketId, selectedAlbumIds)
    } else {
      downloadAlbums(bucketId)
    }
  }

  return (
    <IconButton
      disabled={disabled}
      color={freemiumRestricted ? 'groupsy700' : 'primary'}
      aria-label="Download Album"
      onClick={onClick}
      id="download-button"
      data-test="download-button"
      sx={{
        padding: useUpdatedAlbumActions ? '4px' : '8px',
        bgcolor: freemiumRestricted ? theme.palette.groupsyPink[50] : 'inherit',
        '&:hover': () =>
          freemiumRestricted
            ? {
                bgcolor: theme.palette.groupsyPink[50],
                cursor: 'default'
              }
            : {}
      }}
    >
      {loading && <CircularProgress size={20} />}
      {!loading && (
        <Tooltip title="Download Album" arrow>
          <DownloadIcon />
        </Tooltip>
      )}
    </IconButton>
  )
}
